import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pizza, Store, MapPin, Search as SearchIcon } from "lucide-react";
import api from "../../lib/api";
import ListPageLayout, { SkeletonGrid, EmptyState, FilterChip } from "../../components/smart/ListPageLayout";
import ResultCard from "../../components/smart/ResultCard";
import { useGeo, distanceKm, fmtDistance } from "../../lib/homeMode";
import { CATEGORY_LABELS, categoryKey } from "../../components/map/categoryIcons";

const VARIANTS = {
  food: { title: "Ordina da mangiare", subtitle: "Ristoranti e locali del territorio che consegnano", icon: Pizza, onlyOrderable: true },
  activities: { title: "Attività", subtitle: "Tutte le attività locali di Fuma c'anduma", icon: Store, onlyOrderable: false },
  nearby: { title: "Vicino a te", subtitle: "Le attività più vicine alla tua posizione", icon: MapPin, onlyOrderable: false, byDistance: true },
};

const SORTS = [
  { value: "popular", label: "Più popolari" },
  { value: "rating", label: "Valutazione" },
  { value: "distance", label: "Distanza" },
  { value: "name", label: "Nome (A-Z)" },
];

export default function BusinessList({ variant }) {
  const cfg = VARIANTS[variant] || VARIANTS.activities;
  const navigate = useNavigate();
  const geo = useGeo();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState(cfg.byDistance ? "distance" : "popular");

  useEffect(() => {
    api.get("/public/map").then(({ data }) => setItems(data.pois || [])).catch(() => setItems([]));
  }, []);

  const categories = useMemo(() => {
    if (!items) return [];
    const keys = [...new Set(items.map((p) => categoryKey(p.category)).filter(Boolean))];
    return keys.slice(0, 12);
  }, [items]);

  const shown = useMemo(() => {
    if (!items) return null;
    let list = items.slice();
    if (cfg.onlyOrderable) list = list.filter((p) => p.orderable);
    if (cat !== "all") list = list.filter((p) => categoryKey(p.category) === cat);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.category || "").toLowerCase().includes(s) || (p.zone || "").toLowerCase().includes(s));
    }
    const withDist = list.map((p) => ({ ...p, _d: geo ? distanceKm(geo, { lat: p.lat, lng: p.lng }) : null }));
    const s = cfg.byDistance ? "distance" : sort;
    withDist.sort((a, b) => {
      if (s === "distance") return (a._d ?? 1e9) - (b._d ?? 1e9);
      if (s === "rating") return (b.rating || 0) - (a.rating || 0);
      if (s === "name") return (a.name || "").localeCompare(b.name || "");
      return (b.popular ? 1 : 0) - (a.popular ? 1 : 0) || (b.rating || 0) - (a.rating || 0);
    });
    return withDist;
  }, [items, cat, q, sort, geo, cfg]);

  const chips = [
    <FilterChip key="all" testid="chip-all" active={cat === "all"} onClick={() => setCat("all")}>Tutte</FilterChip>,
    ...categories.map((k) => (
      <FilterChip key={k} testid={`chip-${k}`} active={cat === k} onClick={() => setCat(k)}>{CATEGORY_LABELS[k] || k}</FilterChip>
    )),
  ];

  return (
    <ListPageLayout
      title={cfg.title}
      subtitle={cfg.subtitle + (cfg.byDistance && !geo ? " · attiva la geolocalizzazione per l'ordine per distanza" : "")}
      icon={cfg.icon}
      search={q}
      onSearch={setQ}
      searchPlaceholder="Cerca per nome, categoria o zona…"
      sort={sort}
      onSort={setSort}
      sortOptions={cfg.byDistance ? null : SORTS}
      chips={chips}
    >
      {shown === null ? (
        <SkeletonGrid />
      ) : shown.length === 0 ? (
        <EmptyState icon={SearchIcon} message="Nessun risultato. Prova a cambiare ricerca o filtri." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="business-list">
          {shown.map((p) => (
            <ResultCard
              key={p.id}
              testid={`biz-card-${p.id}`}
              image={p.image}
              fallbackIcon={Store}
              title={p.name}
              subtitle={`${CATEGORY_LABELS[categoryKey(p.category)] || p.category}${p.zone ? ` · ${p.zone}` : ""}`}
              statusLabel={p.orderable ? "Aperto" : "In arrivo"}
              statusType={p.orderable ? "open" : "arriving"}
              extraBadge={p.has_offer ? "Offerta" : (p.popular ? "Popolare" : null)}
              rating={p.rating}
              distance={fmtDistance(p._d)}
              onOpen={() => navigate(`/attivita/${p.id}`)}
              onMap={() => navigate("/esplora")}
            />
          ))}
        </div>
      )}
    </ListPageLayout>
  );
}
